/**
 * Manager Passport Page
 * 
 * Displays manager intelligence metrics, intervention effectiveness,
 * coaching impact, and system health. Catalyst+ feature.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { ManagerSnapshotCard } from '@/components/manager-passport/ManagerSnapshotCard';
import { ManagerInterventionEffectiveness } from '@/components/manager-passport/ManagerInterventionEffectiveness';
import { ManagerLoadManagement } from '@/components/manager-passport/ManagerLoadManagement';
import { ManagerCoachingImpact } from '@/components/manager-passport/ManagerCoachingImpact';
import { ManagerSystemHealth } from '@/components/manager-passport/ManagerSystemHealth';
import { ManagerLearningRecommendations } from '@/components/manager-passport/ManagerLearningRecommendations';
import { PassportCertificationsSection } from '@/components/passport/PassportCertificationsSection';

import { useManagerIntelligenceStats, useCanViewManagerStats } from '@/hooks/useManagerIntelligenceStats';
import { RequireFeature } from '@/components/access/RequireFeature';
import { useAuth } from '@/contexts/AuthContext';

export default function ManagerPassport() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { activeShop } = useAuth();

  // Fetch manager profile info
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['manager-profile', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .eq('id', userId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Check visibility permissions (Tweak #1)
  const { data: accessInfo, isLoading: accessLoading } = useCanViewManagerStats(userId);

  // Fetch intelligence stats
  const { data: stats, isLoading: statsLoading } = useManagerIntelligenceStats(userId);

  const isLoading = profileLoading || accessLoading || statsLoading;

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading manager profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Manager profile not found</p>
          <Button onClick={handleGoBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Access denied view (Tweak #1: Visibility rules)
  if (!accessInfo?.canView) {
    return (
      <div className="container mx-auto p-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleGoBack}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Access Restricted</CardTitle>
            <CardDescription>
              {accessInfo?.isSelfView 
                ? accessInfo?.reason === 'self_view_not_enabled'
                  ? 'Self-view has not been enabled by the shop owner yet.'
                  : 'Self-view is pending. Please check back after the unlock period.'
                : 'Only shop owners can view manager intelligence profiles.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <RequireFeature feature="managerPassports" fallback={
      <div className="container mx-auto p-6 text-center">
        <p className="text-muted-foreground">Manager Passports require Catalyst tier or higher.</p>
      </div>
    }>
      <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-6xl pb-20 overflow-y-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleGoBack}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Section 1: Snapshot Card */}
        <ManagerSnapshotCard 
          profile={profile}
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 2: Intervention Effectiveness */}
        <ManagerInterventionEffectiveness 
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 3: Load Management (Catalyst+) */}
        <ManagerLoadManagement 
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 4: Coaching Impact */}
        <ManagerCoachingImpact 
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 5: System Health */}
        <ManagerSystemHealth 
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 6: Learning Recommendations */}
        <ManagerLearningRecommendations 
          userId={userId!}
        />

        {/* Certifications */}
        <PassportCertificationsSection
          userId={userId!}
          isOwner={accessInfo?.isSelfView || false}
        />
      </div>
    </RequireFeature>
  );
}
